import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, Text } from '@chakra-ui/react';

import { PasswordChanged, ResetPassword } from '../components';
import { useSetNewPasswordMutation } from '../../../services';
// Canonical envelope helpers. These used to be module-local copies here (the
// third re-invention of the same check in this repo); they now live in
// src/utils/api-response.ts, whose header documents what baseQueryWithReAuth
// already reports globally — read it before adding a toast to this page.
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
} from '../../../utils/api-response';

// Fixed, human-readable copy. The backend's `message`/`details` are NEVER
// rendered: the activation endpoint distinguishes TOKEN NOT EXIST, TOKEN
// ALREADY CONSUMED and LINK EXPIRED in those fields, and echoing them would
// turn this anonymous page into an oracle that confirms whether a guessed
// token ever existed. One string covers all three, so the states stay
// indistinguishable in the UI. The raw payload goes to the console instead.
const ACTIVATION_FAILED_MESSAGE =
  'We could not set your password. This activation link may have expired or already been used — please ask your administrator to send you a new invitation.';
const NETWORK_FAILED_MESSAGE =
  'We could not reach the server, so your password was not set. Please check your connection and try again.';

export const NewPassword = () => {
  const [content, setContent] = useState<'resetPassword' | 'passwordChanged'>(
    'resetPassword'
  );
  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Router state is the ONLY carrier of the activation token, and it does not
  // survive a refresh or a directly-typed URL. Read it defensively — the old
  // `const { activationToken } = state as {...}` threw on a null state and
  // white-screened the whole page.
  const { state } = useLocation();
  const activationToken = (
    state as { activationToken?: string } | null | undefined
  )?.activationToken;

  const [setNewPassword, { isLoading }] = useSetNewPasswordMutation();

  const { register, getValues, handleSubmit, setFocus } = useForm<{
    password: string;
    confirmPassword: string;
  }>();

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<{
    password: string;
    confirmPassword: string;
  }> = async data => {
    setIsPasswordMatch(getValues('password') === getValues('confirmPassword'));
    if (getValues('password') !== getValues('confirmPassword'))
      return setFocus('confirmPassword');

    // Clear any error from a previous attempt so a retry starts clean.
    setErrorMessage('');

    try {
      // No `.unwrap()`: it resolves for a body-level failure and throws away
      // the envelope we have to branch on. Await the trigger and classify it.
      const response = await setNewPassword({
        password: data.password,
        activationToken: activationToken as string,
      });

      const outcome = classifyMutationResult(response);

      if (outcome !== MutationResult.SUCCESS) {
        const payload = extractResponsePayload(response);
        // Diagnosis detail stays in the console; the user sees fixed copy.
        console.error('setNewPassword rejected', {
          outcome,
          errorCode: payload.errorCode,
        });
        setErrorMessage(
          outcome === MutationResult.TRANSPORT_FAILURE
            ? NETWORK_FAILED_MESSAGE
            : ACTIVATION_FAILED_MESSAGE
        );
        // Stay on the form; react-hook-form state is untouched so both password
        // fields keep what the user typed.
        return;
      }

      setContent('passwordChanged');

      // TODO: add lotti animation
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      // Belt-and-braces path for a genuine throw — serialisation, an aborted
      // request. It must SET AN ERROR, not only log, or the user is stranded.
      console.error('rejected', error);
      setErrorMessage(NETWORK_FAILED_MESSAGE);
    }
  };

  // Same alert the forget-password flow already ships. The PAGE owns it, so the
  // shared ResetPassword component (used by that flow too) needs no error prop.
  const errorAlert = errorMessage ? (
    <Box
      role="alert"
      mb="6"
      px="4"
      py="3"
      bg="red.50"
      borderWidth="1px"
      borderColor="red.200"
      borderRadius="md"
    >
      <Text color="red.600" fontSize="sm">
        {errorMessage}
      </Text>
    </Box>
  ) : null;

  // No token means there is nothing this page can submit — a refresh, a
  // bookmark, or a direct visit. Send the user to an entry point that can mint
  // a fresh credential instead of rendering a form that cannot work. Declared
  // after every hook so the hook order stays stable across renders.
  if (!activationToken) {
    return <Navigate to="/forget-password" replace />;
  }

  if (content === 'resetPassword') {
    return (
      <Box maxW="24rem" mx="auto">
        {errorAlert}
        <ResetPassword
          isFirstPassword
          register={register}
          isPasswordMatch={isPasswordMatch}
          onSubmit={handleSubmit(onSubmit)}
          isLoading={isLoading}
        />
      </Box>
    );
  } else {
    return <PasswordChanged isActivatingAccount />;
  }
};
